/**
 * CustomBotaoDitado.tsx — o microfone: fala-se, e o texto entra no campo.
 *
 * É o DITADO DO PRÓPRIO NAVEGADOR (`SpeechRecognition`), sem servidor e sem
 * custo: nasceu na conversa (`CustomMensagens`) e virou peça no segundo uso —
 * o formulário de despesa no celular, a pedido dele em 26/09/2026, porque no
 * celular, em campo, falar é mais fácil que digitar.
 *
 * ⚠️ O QUE DEPENDE DO APARELHO: Chrome no Android e no computador fazem; Safari
 * no iPhone faz a partir do iOS 14.5; Firefox não. Onde não existe, o botão
 * aparece desabilitado com a explicação no `title` — e não some, para ninguém
 * achar que ele foi esquecido numa tela e lembrado em outra.
 *
 * O texto ditado ENTRA DEPOIS do que já estava escrito, nunca por cima.
 */
import { useRef, useState } from 'react';
import { toast } from 'sonner';

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult:
    | ((ev: {
        resultIndex: number;
        results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }>;
      }) => void)
    | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  start: () => void;
  stop: () => void;
}

/** O reconhecedor do navegador, ou `null` onde ele não existe. */
function reconhecedor(): (new () => SpeechRecognitionLike) | null {
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/**
 * O ditado como gancho — para quem desenha o próprio botão (a conversa).
 * `texto` é o que o campo tem AGORA; `aoMudar` recebe o texto com o ditado.
 */
export function useDitado(texto: string, aoMudar: (novo: string) => void) {
  const Voz = reconhecedor();
  const [ouvindo, setOuvindo] = useState(false);
  const vozRef = useRef<SpeechRecognitionLike | null>(null);

  function alternar() {
    if (!Voz) return;
    if (ouvindo) {
      vozRef.current?.stop();
      return;
    }
    const r = new Voz();
    r.lang = 'pt-BR';
    r.continuous = true;
    r.interimResults = false;
    const base = texto;
    let ditado = '';
    r.onresult = (ev) => {
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const res = ev.results[i];
        if (res.isFinal) ditado += (ditado ? ' ' : '') + res[0].transcript.trim();
      }
      aoMudar((base ? base.replace(/\s*$/, ' ') : '') + ditado);
    };
    r.onend = () => {
      setOuvindo(false);
      vozRef.current = null;
    };
    r.onerror = () => {
      setOuvindo(false);
      vozRef.current = null;
      toast.error('O ditado não funcionou neste aparelho.');
    };
    vozRef.current = r;
    setOuvindo(true);
    r.start();
  }

  return { disponivel: Boolean(Voz), ouvindo, alternar };
}

/**
 * O botão pronto, para ir ao lado de um campo dentro de um `input-group`:
 *
 *   <div className="input-group">
 *     <div className="form-floating">…input…</div>
 *     <CustomBotaoDitado texto={v} aoMudar={setV} />
 *   </div>
 */
export function CustomBotaoDitado({
  texto,
  aoMudar,
}: {
  texto: string;
  aoMudar: (novo: string) => void;
}) {
  const d = useDitado(texto, aoMudar);
  return (
    <button
      type="button"
      className={`btn ${d.ouvindo ? 'btn-theme' : 'btn-outline-theme'}`}
      title={
        d.disponivel
          ? d.ouvindo
            ? 'Parar o ditado'
            : 'Falar em vez de digitar'
          : 'O ditado não existe neste navegador'
      }
      aria-label={d.ouvindo ? 'Parar o ditado' : 'Ditar'}
      disabled={!d.disponivel}
      onClick={d.alternar}
    >
      <i className={`bi ${d.ouvindo ? 'bi-mic-fill' : 'bi-mic'}`}></i>
    </button>
  );
}
